/**
 * Home's left rail: the inbox of things that want the attorney. The briefing
 * (Morning / Afternoon / Evening by local time) is the first row, then one
 * section per type, labeled once. Approvals waiting on the same client roll
 * up into one row. A single Display menu holds the Show filter and Mark All
 * as Read.
 */
import { CircleCheck, SlidersHorizontal, Sparkles } from "lucide-react";
import { useEffect, useState, type FC } from "react";

import { HeaderIconButton } from "@/components/app-header";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { InboxRow, RailSection } from "@/components/ui/inbox-row";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";
import { cn } from "@/lib/utils";

import { briefingTitle } from "../greeting";
import { groupByKind, rollUp } from "./homeKinds";
import {
  rightLabel,
  TRIAGE_FILTERS,
  waitsOnAttorney,
  type TriageFilter,
  type TriageItem
} from "./triageModel";
import { BRIEFING_ID, type HomeTriage } from "./useHomeTriage";

export const RAIL_TICK_MS = 60_000;

const SkeletonRows: FC = () => (
  <div className="flex flex-col gap-0.5 py-1" data-testid="for-review-loading">
    {Array.from({ length: 8 }, (_, i) => (
      <div key={i} className="flex h-11 items-center gap-2 px-2">
        <Skeleton className="size-5 rounded-xs" />
        <div className="flex flex-1 flex-col gap-1.5">
          <Skeleton
            className="h-3.5"
            style={{ width: `${70 - (i % 3) * 12}%` }}
          />
          <Skeleton className="h-3 w-2/5" />
        </div>
        <Skeleton className="h-3 w-7" />
      </div>
    ))}
  </div>
);

/** Re-render once a minute so "18m" labels keep up. */
const useMinuteTick = (): Date => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), RAIL_TICK_MS);
    return () => window.clearInterval(id);
  }, []);
  return now;
};

type Props = {
  triage: HomeTriage;
  isLoading: boolean;
  /** Briefing row subtitle detail (changes since yesterday). */
  briefingChanges: number | null;
  briefingAt: string | null;
  /** The briefing row is selected only while the Briefing view shows. */
  briefingSelected: boolean;
  onOpenBriefing: () => void;
  className?: string;
};

export const ForReviewRail: FC<Props> = ({
  triage,
  isLoading,
  briefingChanges,
  briefingAt,
  briefingSelected,
  onOpenBriefing,
  className
}) => {
  const now = useMinuteTick();
  const {
    visible,
    filter,
    setFilter,
    isUnread,
    unreadCount,
    briefingRead,
    markAllRead,
    selectedId,
    select
  } = triage;
  const briefingTime = briefingAt
    ? new Date(briefingAt).toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit"
      })
    : "";
  const sections = groupByKind(visible).filter((g) => g.rows.length > 0);

  const itemRow = (t: TriageItem) => {
    const label = rightLabel(t, now);
    return (
      <InboxRow
        key={t.id}
        title={t.title}
        detail={t.clientName}
        avatarName={t.clientName}
        time={label}
        trailing={
          t.overdue && label ? (
            <StatusBadge tone="danger">{label}</StatusBadge>
          ) : undefined
        }
        unread={isUnread(t) && !t.overdue}
        selected={selectedId === t.id}
        onClick={() => select(t.id)}
        data-testid={`triage-row-${t.id}`}
      />
    );
  };

  return (
    <aside
      className={cn(
        "flex h-full min-h-0 w-70 shrink-0 flex-col border-r border-line-2 bg-chrome",
        className
      )}
      data-testid="for-review-rail"
      aria-label="For Review"
    >
      <header className="flex h-10 shrink-0 items-center gap-1.5 pr-1.5 pl-4">
        <span className="text-base font-medium text-fg">For Review</span>
        {isLoading ? null : (
          <span
            className="text-base text-fg-4 tabular-nums"
            data-testid="for-review-count"
          >
            {visible.filter(waitsOnAttorney).length}
          </span>
        )}
        <span className="flex-1" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <HeaderIconButton
              label="Display"
              pressed={filter !== "all" ? true : undefined}
              data-testid="for-review-display"
            >
              <SlidersHorizontal className="size-4" />
            </HeaderIconButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel>Show</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={filter}
              onValueChange={(v) => setFilter(v as TriageFilter)}
            >
              {TRIAGE_FILTERS.map((f) => (
                <DropdownMenuRadioItem key={f.id} value={f.id}>
                  {f.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={markAllRead}
              disabled={unreadCount === 0}
            >
              Mark All as Read{unreadCount ? ` (${unreadCount})` : ""}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {isLoading ? (
          <SkeletonRows />
        ) : (
          <div className="flex flex-col gap-2">
            <InboxRow
              title={briefingTitle(now)}
              detail={`Casey${
                briefingChanges !== null
                  ? ` · ${briefingChanges} change${
                      briefingChanges === 1 ? "" : "s"
                    }`
                  : ""
              }`}
              media={
                <span
                  className="inline-flex size-5 shrink-0 items-center justify-center text-fg-2"
                  data-testid="briefing-row-icon"
                >
                  <Sparkles className="size-3.75" />
                </span>
              }
              time={briefingTime}
              unread={!briefingRead}
              selected={briefingSelected && selectedId === BRIEFING_ID}
              onClick={() => {
                select(BRIEFING_ID);
                onOpenBriefing();
              }}
              data-testid="triage-row-briefing"
            />
            {sections.map((g) => (
              <RailSection
                key={g.kind}
                kind={g.kind}
                count={g.rows.length}
                data-testid={`triage-group-${g.kind}`}
              >
                {rollUp(g.rows).map((entry) =>
                  entry.kind === "item" ? (
                    itemRow(entry.item)
                  ) : (
                    <InboxRow
                      key={entry.id}
                      title={`${entry.items.length} Approvals Waiting`}
                      detail={entry.items[0].clientName}
                      avatarName={entry.items[0].clientName}
                      time={rightLabel(entry.items[0], now)}
                      unread={entry.items.some(isUnread)}
                      selected={entry.items.some((t) => t.id === selectedId)}
                      onClick={() => select(entry.items[0].id)}
                      data-testid={`triage-row-${entry.id}`}
                    />
                  )
                )}
              </RailSection>
            ))}
            {sections.length === 0 ? (
              <EmptyState
                icon={CircleCheck}
                title={
                  filter === "all"
                    ? "You're All Caught Up"
                    : "Nothing Matches This Filter"
                }
                description="New approvals, mentions and deadlines land here."
                className="py-10"
                data-testid="for-review-empty"
              />
            ) : null}
          </div>
        )}
      </div>
    </aside>
  );
};
